import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, from, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { Paginator } from '../interfaces/paginator';
import { DatabaseService } from './database.service';
import { FilaDBService } from 'src/app/database/fila-db.service';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { FileOpener } from '@capacitor-community/file-opener';
import { Capacitor } from '@capacitor/core';
import write_blob from "capacitor-blob-writer";
import { AlertController } from '@ionic/angular';

export function startWithTap<T>(callback: () => void) {
    return (source: Observable<T>) => of({}).pipe(tap(callback), switchMap((o) => source));
}

@Injectable({
    providedIn: 'root'
})
export abstract class BaseService {

    abstract classname: string;

    private static _online: boolean | null = null;
    private static onlineReadyPromise: Promise<boolean> | null = null;
    private static onlineReadyResolve: ((online: boolean) => void) | null = null;

    /**
     * O AuthGuard continua sendo a fonte de verdade da conexão.
     * Ele pode continuar usando BaseService.online = true/false.
     * O setter abaixo libera as chamadas que ficaram aguardando a primeira resolução.
     */
    public static get online(): boolean | null{
        return BaseService._online;
    }

    public static set online(value: boolean | null){
        BaseService._online = value;

        if(value !== null && BaseService.onlineReadyResolve){
            BaseService.onlineReadyResolve(value);
            BaseService.onlineReadyResolve = null;
            BaseService.onlineReadyPromise = null;
        }
    }

    private static waitOnlineStatus(): Promise<boolean>{
        if(BaseService._online !== null){
            return Promise.resolve(BaseService._online);
        }

        if(!BaseService.onlineReadyPromise){
            BaseService.onlineReadyPromise = new Promise<boolean>((resolve) => {
                BaseService.onlineReadyResolve = resolve;
            });
        }

        return BaseService.onlineReadyPromise;
    }

    public getting: boolean = false;

    private items: Paginator = {data: [], next_page_url: null, current_page: 1};
    private path: string | null = null;
    private params: any = {};
    private headers: any = {};

    protected url: string = environment.url;
    public route: string = '';

    public items$: Observable<Paginator>;
    public subjectItems: BehaviorSubject<any> = new BehaviorSubject({data: [], current_page: 1, next_page_url: null});
    // public online = new BehaviorSubject(true);

    public database?: DatabaseService;
    protected DB: FilaDBService = new FilaDBService();

    private parseHeaders(headers: any = null){
        const h = headers ? {...headers} : {};
        const awaitLocalSync = h.awaitLocalSync === true || h.awaitLocalSync === 'true';
        delete h.awaitLocalSync;
        return {headers: h, awaitLocalSync};
    }

	constructor(protected http: HttpClient, private alertController: AlertController) {
        this.items$ = this.subjectItems.asObservable().pipe(
            tap(() => {
                this.getting = true;
            }),
            switchMap(
                () => {
                    this.params.page = this.subjectItems.value.current_page;
                    return this.get(this.path, this.params, this.headers).pipe(
                        tap(() => {
                            this.getting = false;
                        }),
                        map((items: Paginator) => {
                            if(items.current_page == 1)
                                this.items = items;
                            else{
                                this.items.data = this.items.data.concat(items.data);
                                this.items.current_page = items.current_page;
                                this.items.next_page_url = items.next_page_url;
                            }

                            if(this.subjectItems.value.event)
                                this.subjectItems.value.event?.target.complete();
                            return this.items;
                        })
                    )
                }
            )
        )
	}

    init(path: string | null = null, params: any = null, headers: any = null){
        this.path = path;
        this.headers = headers;
        this.params = params;
    }

	get(path: string | null = null, params: any = null, headers: any = null): Observable<any>{
        this.getting = true;
        const options = this.parseHeaders(headers);

        return from(BaseService.waitOnlineStatus()).pipe(
            switchMap((online) => {
                if(online || !this.database){
                    return this.http.get<any>(this.url + this.route + (path ? '/' + path: ''), {params: params || {}, headers: options.headers}).pipe(
                        switchMap((r) => {
                            this.getting = false;
                            if(this.database && r && r.data){
                                if(options.awaitLocalSync){
                                    return from(this.database.sincArray(r.data)).pipe(
                                        map(() => r)
                                    );
                                }else{
                                    this.database.sincArray(r.data).catch(() => {});
                                }
                            }
                            return of(r);
                        }),
                    );
                }

                return from(this.database?.paginate(params || {}) as any).pipe(
                    tap(() => {
                        this.getting = false;
                    }),
                    catchError((error) => {
                        this.getting = false;

                        if(error?.code === 'LOCAL_RECORD_TOO_LARGE'){
                            this.alertController.create({
                                mode: 'ios',
                                header: 'Conecte-se à internet',
                                message: error.message || 'Existem registros offline que precisam ser atualizados antes de abrir esta lista.'
                            }).then((alert) => alert.present());

                            return of({
                                current_page: 1,
                                data: [],
                                next_page_url: null
                            });
                        }

                        return throwError(() => error);
                    })
                );
            })
        );
	}

    sync(path: string | null = null, params: any = null, headers: any = null): Observable<any>{
        const options = this.parseHeaders(headers);

        return from(BaseService.waitOnlineStatus()).pipe(
            switchMap((online) => {
                if(online && this.database){
                    return this.http.get<any>(this.url + this.route + '/sync' + (path ? '/' + path: ''), {params: params || {}, headers: options.headers}).pipe(
                        switchMap((r) => {
                            if(options.awaitLocalSync){
                                return from(this.database?.sync(r || []) as any).pipe(map(() => r));
                            }else{
                                this.database?.sync(r || []).catch(() => {});
                            }
                            return of(r);
                        })
                    );
                }

                return of([]);
            })
        );
	}

	show(id: any, params: any = null, headers: any = null): Observable<any>{
        return from(BaseService.waitOnlineStatus()).pipe(
            switchMap((online) => {
                if(online || !this.database){
                    return this.http.get<any>(this.url + this.route + '/' + id + '/show', {params: params || {}, headers: headers}).pipe(
                        tap((r) => {
                            if(this.database){
                                this.database.sinc(r);
                            }
                        })
                    );
                }

                return from(this.database.getByKey(id) as any).pipe(
                    catchError((error) => {
                        if(error?.code === 'LOCAL_RECORD_TOO_LARGE'){
                            this.alertController.create({
                                mode: 'ios',
                                header: 'Conecte-se à internet',
                                message: error.message || 'Este registro offline precisa ser atualizado antes de ser aberto.'
                            }).then((alert) => alert.present());

                            return EMPTY;
                        }

                        return throwError(() => error);
                    })
                );
            })
        );
	}

	post(data: any, path: string | null = null, params: any = null, headers: any = null): Observable<any>{
        return from(BaseService.waitOnlineStatus()).pipe(
            switchMap((online) => {
                if(online || !this.database){
                    return this.http.post<any>(this.url + this.route + (path ? '/' + path: ''), data, {params: params || {}, headers: headers}).pipe(
                        tap((r) => {
                            if(this.database && r && !path && (!params || !params.skipbase)){
                                this.database.sinc(r);
                            }
                        })
                    );
                }

                return from(this.database.put(data)).pipe(
                    switchMap((r) => {
                        return from(this.addToFila(this.route, 'POST', r, path));
                    })
                );
            })
        );
	}

	put(data: any, path: string | null = null, params: any = null, headers: any = null): Observable<any>{
        return from(BaseService.waitOnlineStatus()).pipe(
            switchMap((online) => {
                if(online || !this.database){
                    return this.http.put<any>(this.url + this.route + '/' + data.id + (path ? '/' + path: ''), data, {params: params || {}, headers: headers}).pipe(
                        tap((r) => {
                            if(this.database && r && r.id == data.id && !path){
                                this.database.sinc(r);
                            }
                        })
                    );
                }

                return from(this.database.put(data)).pipe(
                    switchMap((r) => {
                        return from(this.addToFila(this.route, 'PUT', r, path));
                    })
                );
            })
        );
	}

	delete(data: any, path: string | null = null, params: any = null, headers: any = null): Observable<any>{
        return from(BaseService.waitOnlineStatus()).pipe(
            switchMap((online) => {
                if(online || !this.database){
                    return this.http.delete<any>(this.url + this.route + '/' + data.id + (path ? '/' + path: ''), {params: params || {}, headers: headers}).pipe(
                        tap((r) => {
                            if(this.database && r && r.id == data.id && !path){
                                this.database.del(r[this.database.indices[0].field], 'by_id');
                            }
                        })
                    );
                }

                if(data[this.database.indices[0].field]){
                    return from(this.database.del(data[this.database.indices[0].field], 'by_id')).pipe(
                        switchMap(() => {
                            return from(this.addToFila(this.route, 'DELETE', data, path));
                        })
                    );
                }

                return from(this.database.del(data[this.database.keyPath])).pipe(
                    switchMap(() => {
                        return from(this.delToFila(this.route, data));
                    })
                );
            })
        );
	}

    blob(path: string | null = null, params: any = null, headers: any = null, body: any = null){
        BaseService.waitOnlineStatus().then((online) => {
            if(!online){
                return;
            }

            const url = this.url + this.route + (path ? '/' + path: '');
            const options = {params: params || {}, headers: headers, responseType: 'blob' as const};
            const report = body ? this.http.post(url, body, options) : this.http.get(url, options);
            report.subscribe((resp) => {
                if(Capacitor.isNativePlatform()){
                    write_blob({
                        path: params?.tipo == 'EXCEL' ? 'report.xlsx' : "report.pdf",
                        directory: Directory.Data,
                        blob: resp,
                        fast_mode: true,
                        recursive: true,
                        on_fallback(error) {
                            console.error(error);
                        }
                    }).then(() => {
                        Filesystem.getUri({
                            path: params?.tipo == 'EXCEL' ? 'report.xlsx' : "report.pdf",
                            directory: Directory.Data
                        }).then((file) => {
                            FileOpener.open({
                                filePath: file.uri
                            }).catch(() => {
                                this.alertController.create({mode: 'ios', header: 'Erro ao Abrir', message: 'Verifique se você possui o app necessário para abrir o arquivo.'}).then(a => a.present())
                            });
                        });
                    });

                    // Filesystem.writeFile({
                    //     path: 'report.pdf',
                    //     data: resp as any,
                    //     directory: Directory.Data,
                    // }).then(writ => {
                    //     FileOpener.open({
                    //         filePath: writ.uri
                    //     });
                    // });
                }else{
                    var downloadURL = window.URL.createObjectURL(resp);
                    var link = document.createElement('a');
                    link.href = downloadURL;
                    link.download = params?.tipo == 'EXCEL' ? 'report.xlsx' : "report.pdf";
                    link.click();
                }
            });
        });
    }

    public novo() {
		return {} as any;
	}

    public hasMore(){
        return this.items.next_page_url ? true : false;
    }

    public getItems(){
        return this.items;
    }

    public addToFila(resource: string, method: 'POST' | 'PUT' | 'DELETE', data: any, path: string | null = null): Promise<any>{
        return this.DB.put({
            id : resource + "-" + method + "-" + data[this.database?.keyPath as string],
            service : this.classname,
            db : this.database?.classname,
            table: this.database?.table,
            key : this.database?.keyPath,
            resource : resource,
            method : method,
            path: path,
            data : data
        });
    }

    private async delToFila(resource: string, data: any): Promise<any>{
        const key = data[this.database?.keyPath as string];
        await this.DB.del(resource + "-POST-" + key).catch(() => null);
        return this.DB.del(resource + "-PUT-" + key).catch(() => null);
    }

}
